import { useCallback, useEffect, useState } from 'react';
import {
  Loader,
  AlertCircle,
  Link2,
  Plus,
  ExternalLink,
  Pencil,
  Trash2,
  X,
  Check,
  Megaphone,
  MessageCircle,
  MessagesSquare,
  Video,
  FileText,
  Globe,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { eventTeamApiService } from '../../services/eventTeamApi';
import type { EventWebLink, EventWebLinkKind } from '../../types/eventTeam';
import { EVENT_WEB_LINK_KINDS } from '../../types/eventTeam';
import type { EventTeamPerms } from '../../utils/eventTeamPermissions';
import { CreatorProfile } from '../CreatorProfile';

const KIND_META: Record<EventWebLinkKind, { icon: React.ComponentType<{ className?: string }>; color: string; label: string }> = {
  announcement: { icon: Megaphone, color: 'text-amber-300', label: 'Announcement' },
  discord: { icon: MessageCircle, color: 'text-indigo-300', label: 'Discord' },
  forum: { icon: MessagesSquare, color: 'text-emerald-300', label: 'Forum' },
  video: { icon: Video, color: 'text-red-300', label: 'Video' },
  doc: { icon: FileText, color: 'text-sky-300', label: 'Doc' },
  other: { icon: Globe, color: 'text-glass-300', label: 'Other' },
};

export interface EventWebLinksSectionProps {
  targetType: 'event' | 'project';
  targetId: string;
  perms: EventTeamPerms;
  refreshNonce?: number;
  onChanged?: () => void;
}

/** Announcement/Discord/forum/video/doc URLs attached to events and projects (guide §1.4). */
export const EventWebLinksSection = ({
  targetType,
  targetId,
  perms,
  refreshNonce = 0,
  onChanged,
}: EventWebLinksSectionProps) => {
  const [links, setLinks] = useState<EventWebLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newKind, setNewKind] = useState<EventWebLinkKind>('announcement');
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [editKind, setEditKind] = useState<EventWebLinkKind>('other');

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await eventTeamApiService.listWebLinks(targetType, targetId);
      setLinks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load web links');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
  }, [refreshNonce, load]);

  const handleAdd = async () => {
    if (!newUrl.trim()) return;
    setAdding(true);
    try {
      await eventTeamApiService.createWebLink(targetType, targetId, {
        url: newUrl.trim(),
        label: newLabel.trim() || null,
        kind: newKind,
      });
      setNewUrl('');
      setNewLabel('');
      setNewKind('announcement');
      setShowAdd(false);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add web link');
    } finally {
      setAdding(false);
    }
  };

  const handleSaveEdit = async (link: EventWebLink) => {
    setBusyId(link.id);
    try {
      await eventTeamApiService.updateWebLink(link.id, {
        url: editUrl.trim(),
        label: editLabel.trim() || null,
        kind: editKind,
      });
      setEditingId(null);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update web link');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (link: EventWebLink) => {
    if (!confirm(`Remove link "${link.label || link.url}"?`)) return;
    setBusyId(link.id);
    try {
      await eventTeamApiService.deleteWebLink(link.id);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete web link');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-6 h-6 animate-spin text-accent-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-glass-300 inline-flex items-center gap-2">
          <Link2 className="w-4 h-4 text-emerald-400" />
          Web Links ({links.length})
        </h3>
        {perms.canCreate && (
          <button onClick={() => setShowAdd(!showAdd)} className="glass-button text-xs inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" />
            Add link
          </button>
        )}
      </div>

      {error && (
        <div className="glass-card p-3 border-l-4 border-red-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      {showAdd && (
        <div className="glass-card p-3 flex items-center gap-2 flex-wrap">
          <input
            type="url"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://…"
            className="glass-input text-sm flex-1 min-w-[220px]"
            autoFocus
          />
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Label (optional)"
            className="glass-input text-sm w-44"
          />
          <select
            value={newKind}
            onChange={(e) => setNewKind(e.target.value as EventWebLinkKind)}
            className="glass-input text-sm capitalize"
          >
            {EVENT_WEB_LINK_KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_META[k].label}
              </option>
            ))}
          </select>
          <button
            onClick={() => void handleAdd()}
            disabled={adding || !newUrl.trim()}
            className="glass-button text-xs inline-flex items-center gap-1.5"
          >
            {adding ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Add
          </button>
        </div>
      )}

      {links.length === 0 && !error ? (
        <p className="text-glass-400 text-sm py-4 text-center">No web links.</p>
      ) : (
        <div className="space-y-1.5">
          {links.map((link) => {
            const meta = KIND_META[link.kind] ?? KIND_META.other;
            const Icon = meta.icon;
            const busy = busyId === link.id;
            const isEditing = editingId === link.id;
            return (
              <div key={link.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-glass-100/5 border border-glass-200/10 flex-wrap">
                {isEditing ? (
                  <div className="flex items-center gap-2 flex-1 flex-wrap">
                    <input
                      type="url"
                      value={editUrl}
                      onChange={(e) => setEditUrl(e.target.value)}
                      className="glass-input text-sm flex-1 min-w-[180px]"
                    />
                    <input
                      type="text"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      placeholder="Label"
                      className="glass-input text-sm w-36"
                    />
                    <select
                      value={editKind}
                      onChange={(e) => setEditKind(e.target.value as EventWebLinkKind)}
                      className="glass-input text-sm"
                    >
                      {EVENT_WEB_LINK_KINDS.map((k) => (
                        <option key={k} value={k}>
                          {KIND_META[k].label}
                        </option>
                      ))}
                    </select>
                    <button onClick={() => setEditingId(null)} className="glass-button-secondary !p-1.5">
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => void handleSaveEdit(link)}
                      disabled={busy || !editUrl.trim()}
                      className="glass-button !p-1.5"
                    >
                      {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Icon className={`w-4 h-4 flex-shrink-0 ${meta.color}`} />
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-accent-300 hover:text-accent-200 transition-colors truncate inline-flex items-center gap-1"
                      >
                        {link.label || link.url}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full bg-glass-200/10 ${meta.color}`}>
                        {meta.label}
                      </span>
                      <span className="text-[11px] text-glass-500 inline-flex items-center gap-1">
                        by <CreatorProfile creatorId={link.linked_by} inline /> ·{' '}
                        {formatDistanceToNow(new Date(link.linked_at), { addSuffix: true })}
                      </span>
                    </div>
                    {perms.canCreate && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => {
                            setEditingId(link.id);
                            setEditUrl(link.url);
                            setEditLabel(link.label ?? '');
                            setEditKind(link.kind);
                          }}
                          disabled={busy}
                          className="glass-button-secondary !p-1.5"
                          title="Edit link"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => void handleDelete(link)}
                          disabled={busy}
                          className="glass-button-secondary !p-1.5 text-red-300"
                          title="Delete link"
                        >
                          {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
